import { definePlatformSetting, } from '@crest/platform-contracts/settings';
import { platformBrandAssetReferenceSchema } from '@crest/platform-contracts/brand-assets';
import { platformCustomDomainReferenceSchema } from '@crest/platform-contracts/custom-domains';
import { platformSenderIdentityReferenceSchema } from '@crest/platform-contracts/sender-identities';
import { platformSystemNotificationConfigurationSchema, platformSystemNotificationCopySchema, platformSystemNotificationDefaults, platformSystemNotificationDisplaySchema, } from '@crest/platform-contracts';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';
import { z } from 'zod';
const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const checksumSchema = z.string().regex(/^[0-9a-f]{64}$/);
const approvedThemeFontSchema = z.enum([
    'system',
    'inter',
    'source-sans-3',
    'atkinson-hyperlegible',
]);
function colorChannel(hex, offset) {
    const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}
function colorLuminance(hex) {
    return 0.2126 * colorChannel(hex, 1) +
        0.7152 * colorChannel(hex, 3) +
        0.0722 * colorChannel(hex, 5);
}
export function platformColorContrastRatio(first, second) {
    const firstLuminance = colorLuminance(first);
    const secondLuminance = colorLuminance(second);
    const lighter = Math.max(firstLuminance, secondLuminance);
    const darker = Math.min(firstLuminance, secondLuminance);
    return (lighter + 0.05) / (darker + 0.05);
}
function platformCopyTemplateSchema(requiredPlaceholders, maxLength) {
    return z.string().trim().min(1).max(maxLength).superRefine((value, context) => {
        for (const placeholder of requiredPlaceholders) {
            if (!value.includes(placeholder)) {
                context.addIssue({ code: 'custom', message: `Copy must include ${placeholder}.` });
            }
        }
    });
}
const platformThemePaletteValueSchema = z.strictObject({
    canvas: hexColorSchema,
    surface: hexColorSchema,
    surfaceElevated: hexColorSchema,
    text: hexColorSchema,
    textMuted: hexColorSchema,
    border: hexColorSchema,
    brand: hexColorSchema,
    brandContrast: hexColorSchema,
    success: hexColorSchema,
    warning: hexColorSchema,
    danger: hexColorSchema,
    focus: hexColorSchema,
    attention: hexColorSchema,
    attentionContrast: hexColorSchema,
}).superRefine((value, context) => {
    const surfaces = [
        ['canvas', value.canvas],
        ['surface', value.surface],
        ['surfaceElevated', value.surfaceElevated],
    ];
    for (const [surfaceName, surfaceColor] of surfaces) {
        if (platformColorContrastRatio(value.text, surfaceColor) < 4.5) {
            context.addIssue({
                code: 'custom',
                path: ['text'],
                message: `Primary text must have at least 4.5:1 contrast on ${surfaceName}.`,
            });
        }
        if (platformColorContrastRatio(value.textMuted, surfaceColor) < 3) {
            context.addIssue({
                code: 'custom',
                path: ['textMuted'],
                message: `Muted text must have at least 3:1 contrast on ${surfaceName}.`,
            });
        }
        if (platformColorContrastRatio(value.focus, surfaceColor) < 3) {
            context.addIssue({
                code: 'custom',
                path: ['focus'],
                message: `Focus color must have at least 3:1 contrast on ${surfaceName}.`,
            });
        }
        for (const statusName of ['success', 'warning', 'danger']) {
            if (platformColorContrastRatio(value[statusName], surfaceColor) < 3) {
                context.addIssue({
                    code: 'custom',
                    path: [statusName],
                    message: `${statusName} must have at least 3:1 contrast on ${surfaceName}.`,
                });
            }
        }
    }
    if (platformColorContrastRatio(value.brand, value.brandContrast) < 4.5) {
        context.addIssue({
            code: 'custom',
            path: ['brandContrast'],
            message: 'Brand action text must have at least 4.5:1 contrast on the brand color.',
        });
    }
    if (platformColorContrastRatio(value.attention, value.attentionContrast) < 4.5) {
        context.addIssue({
            code: 'custom',
            path: ['attentionContrast'],
            message: 'Attention action text must have at least 4.5:1 contrast on the attention color.',
        });
    }
});
const platformThemePaletteV2ValueSchema = z.strictObject({
    canvas: hexColorSchema,
    surface: hexColorSchema,
    surfaceElevated: hexColorSchema,
    text: hexColorSchema,
    textMuted: hexColorSchema,
    border: hexColorSchema,
    brand: hexColorSchema,
    brandContrast: hexColorSchema,
    success: hexColorSchema,
    warning: hexColorSchema,
    danger: hexColorSchema,
    focus: hexColorSchema,
});
const platformThemeTokensV2ValueSchema = z.strictObject({
    defaultMode: z.enum(['light', 'dark', 'system']),
    color: platformThemePaletteV2ValueSchema,
    darkColor: platformThemePaletteV2ValueSchema,
    typography: z.strictObject({
        fontFamily: approvedThemeFontSchema,
        baseSizePx: z.number().finite().int().min(14).max(22),
        lineHeight: z.number().finite().min(1.2).max(2),
        headingScale: z.number().finite().min(1.1).max(1.5),
    }),
    card: z.strictObject({
        radiusPx: z.number().finite().int().min(0).max(32),
        paddingPx: z.number().finite().int().min(8).max(40),
        gapPx: z.number().finite().int().min(4).max(40),
        minWidthPx: z.number().finite().int().min(220).max(640),
        shadow: z.enum(['none', 'subtle', 'raised']),
    }),
});
export const platformTenantAppearanceBuiltInPresetIdSchema = z.enum([
    'crest-operational-classic-v1',
    'crest-neutral-v1',
    'field-high-contrast-v1',
    'water-pro-classic-v1',
    'water-pro-classic-v2',
]);
export const platformTenantAppearancePresetDraftRequestSchema = z.strictObject({
    presetId: platformTenantAppearanceBuiltInPresetIdSchema,
});
export const platformThemeTokensValueSchema = z.strictObject({
    presetId: platformTenantAppearanceBuiltInPresetIdSchema.or(z.literal('custom-v1')),
    presetChecksum: checksumSchema,
    defaultMode: z.enum(['light', 'dark', 'system']),
    color: platformThemePaletteValueSchema,
    darkColor: platformThemePaletteValueSchema,
    typography: z.strictObject({
        bodyFontFamily: approvedThemeFontSchema,
        headingFontFamily: approvedThemeFontSchema,
        baseSizePx: z.number().finite().int().min(14).max(22),
        lineHeight: z.number().finite().min(1.2).max(2),
        headingScale: z.number().finite().min(1.1).max(1.5),
    }),
    card: z.strictObject({
        radiusPx: z.number().finite().int().min(0).max(32),
        paddingPx: z.number().finite().int().min(8).max(40),
        gapPx: z.number().finite().int().min(4).max(40),
        minWidthPx: z.number().finite().int().min(220).max(640),
        maxWidthPx: z.number().finite().int().min(280).max(1200).nullable(),
        elevation: z.enum(['none', 'subtle', 'raised']),
    }).superRefine((value, context) => {
        if (value.maxWidthPx !== null && value.maxWidthPx < value.minWidthPx) {
            context.addIssue({
                code: 'custom',
                path: ['maxWidthPx'],
                message: 'Card maximum width cannot be below its minimum width.',
            });
        }
    }),
    control: z.strictObject({
        radiusPx: z.number().finite().int().min(0).max(32),
        minHeightPx: z.number().finite().int().min(44).max(72),
    }),
    button: z.strictObject({
        shape: z.enum(['square', 'rounded', 'pill']),
        radiusPx: z.number().finite().int().min(0).max(999),
        minHeightPx: z.number().finite().int().min(44).max(72),
        fieldActionMinHeightPx: z.number().finite().int().min(56).max(80),
    }).superRefine((value, context) => {
        const validRadius = (value.shape === 'square' && value.radiusPx === 0) ||
            (value.shape === 'pill' && value.radiusPx === 999) ||
            (value.shape === 'rounded' && value.radiusPx >= 2 && value.radiusPx <= 32);
        if (!validRadius) {
            context.addIssue({
                code: 'custom',
                path: ['radiusPx'],
                message: 'Button radius must match its selected shape.',
            });
        }
    }),
    motion: z.strictObject({
        standardDurationMs: z.number().finite().int().min(0).max(1000),
        easing: z.enum(['linear', 'ease', 'ease-in', 'ease-out', 'ease-in-out']),
    }),
}).superRefine((value, context) => {
    const catalogTheme = value.presetId === 'custom-v1'
        ? undefined
        : platformTenantAppearancePresetCatalog[value.presetId];
    if (catalogTheme !== undefined && value.presetChecksum !== catalogTheme.theme.presetChecksum) {
        context.addIssue({
            code: 'custom',
            path: ['presetChecksum'],
            message: 'Built-in preset checksums must match the immutable catalog.',
        });
    }
});
const platformBrandingBusinessAddressSchema = z.strictObject({
    line1: z.string().trim().min(1).max(160),
    line2: z.string().trim().min(1).max(160).nullable(),
    city: z.string().trim().min(1).max(100),
    region: z.string().trim().min(1).max(100),
    postalCode: z.string().trim().min(1).max(24),
    countryCode: z.string().regex(/^[A-Z]{2}$/),
});
const platformBrandAssetSelectionsSchema = z.strictObject({
    lightLogo: platformBrandAssetReferenceSchema.nullable(),
    darkLogo: platformBrandAssetReferenceSchema.nullable(),
    printLogo: platformBrandAssetReferenceSchema.nullable(),
    emailLogo: platformBrandAssetReferenceSchema.nullable(),
    portalLogo: platformBrandAssetReferenceSchema.nullable(),
    documentLogo: platformBrandAssetReferenceSchema.nullable(),
    favicon: platformBrandAssetReferenceSchema.nullable(),
});
export const platformBrandingIdentityValueSchema = z.strictObject({
    applicationName: z.string().trim().min(1).max(80),
    companyDisplayName: z.string().trim().min(1).max(120),
    legalBusinessName: z.string().trim().min(1).max(160).nullable(),
    businessAddress: platformBrandingBusinessAddressSchema.nullable(),
    supportEmail: z.string().email().nullable(),
    supportPhone: z.string().trim().max(40).nullable(),
    assets: platformBrandAssetSelectionsSchema,
});
export const platformCustomDomainSelectionValueSchema = z.strictObject({
    webDomain: platformCustomDomainReferenceSchema.nullable(),
    portalDomain: platformCustomDomainReferenceSchema.nullable(),
});
export const platformSenderIdentitySelectionValueSchema = z.strictObject({
    emailSender: platformSenderIdentityReferenceSchema.nullable(),
    smsSender: platformSenderIdentityReferenceSchema.nullable(),
});
export const platformShellLayoutValueSchema = z.strictObject({
    sidebarWidthPx: z.number().int().min(200).max(420),
    contentMaxWidthPx: z.number().int().min(960).max(1920),
    density: z.enum(['comfortable', 'compact']),
    navigationLabels: z.enum(['full', 'compact']),
});
export const platformAccessibilityPreferencesValueSchema = z.strictObject({
    contrast: z.enum(['standard', 'high']),
    reduceMotion: z.boolean(),
    underlineLinks: z.boolean(),
    focusIndicator: z.enum(['standard', 'enhanced']),
});
export const platformSystemNotificationConfigurationValueSchema = platformSystemNotificationConfigurationSchema;
export const platformSystemNotificationDisplayValueSchema = platformSystemNotificationDisplaySchema;
export const platformSystemNotificationCopyValueSchema = platformSystemNotificationCopySchema;
export const platformMyDayCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(300),
    loadingLabel: z.string().trim().min(1).max(120),
    contributionError: z.string().trim().min(1).max(240),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
});
function migratePlatformMyDayCopyVersionOne(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return value;
    const source = value;
    if (source.description !==
        'Your tenant-aware daily workspace, assembled from the modules available to you.')
        return value;
    return {
        ...source,
        description: 'Today\'s jobs, time, tasks, and priorities in one place.',
    };
}
export const platformCoreCopyValueSchema = z.strictObject({
    navigationProjects: z.string().trim().min(1).max(60),
    navigationGroupCommand: z.string().trim().min(1).max(60),
    navigationGroupProjects: z.string().trim().min(1).max(60),
    navigationGroupField: z.string().trim().min(1).max(60),
    navigationGroupMoney: z.string().trim().min(1).max(60),
    navigationGroupRelationships: z.string().trim().min(1).max(60),
    navigationGroupEstimating: z.string().trim().min(1).max(60),
    navigationGroupReports: z.string().trim().min(1).max(60),
    navigationGroupAdmin: z.string().trim().min(1).max(60),
    accountMenuLabel: z.string().trim().min(1).max(60),
    settingsLabel: z.string().trim().min(1).max(60),
    primaryNavigationLabel: z.string().trim().min(1).max(80),
    skipToContentLabel: z.string().trim().min(1).max(80),
    noAvailableModulesLabel: z.string().trim().min(1).max(160),
    loadingLabel: z.string().trim().min(1).max(120),
    unexpectedErrorLabel: z.string().trim().min(1).max(240),
    configurationTitle: z.string().trim().min(1).max(120),
    configurationDescription: z.string().trim().min(1).max(300),
    configurationSectionLabel: z.string().trim().min(1).max(100),
    configurationHistoryLoadError: z.string().trim().min(1).max(240),
    configurationDraftExistsLabel: z.string().trim().min(1).max(180),
    configurationDraftSchemaOutdatedLabel: z.string().trim().min(1).max(240),
    configurationDraftSavedMessage: platformCopyTemplateSchema(['{version}'], 220),
    configurationAdvancedSummary: z.string().trim().min(1).max(120),
    configurationAdvancedLabel: z.string().trim().min(1).max(120),
    configurationSaveDraftAction: z.string().trim().min(1).max(100),
    configurationPublishAction: z.string().trim().min(1).max(100),
    configurationInitializeBaselineAction: z.string().trim().min(1).max(100),
    configurationInitializeBaselineHelp: z.string().trim().min(1).max(240),
    configurationBaselineRequiredLabel: z.string().trim().min(1).max(220),
    configurationReasonLabel: z.string().trim().min(1).max(120),
    configurationReasonHelp: z.string().trim().min(1).max(240),
    configurationReasonPlaceholder: z.string().trim().min(1).max(180),
    configurationValidatePreviewAction: z.string().trim().min(1).max(100),
    configurationValidateRollbackAction: z.string().trim().min(1).max(100),
    configurationPreviewValidatedMessage: platformCopyTemplateSchema(['{checksum}'], 240),
    configurationPreviewRequiredLabel: z.string().trim().min(1).max(220),
    configurationPublishPermissionRequiredLabel: z.string().trim().min(1).max(220),
    configurationPreviewExpiredError: z.string().trim().min(1).max(240),
    configurationReauthenticationError: z.string().trim().min(1).max(240),
    configurationPreviewTitle: z.string().trim().min(1).max(120),
    configurationPreviewInvalidLabel: z.string().trim().min(1).max(180),
    configurationPreviewCardTitle: z.string().trim().min(1).max(120),
    configurationPreviewCardDescription: z.string().trim().min(1).max(220),
    configurationPreviewAction: z.string().trim().min(1).max(100),
    configurationHistoryTitle: z.string().trim().min(1).max(120),
    configurationNoReleasesLabel: z.string().trim().min(1).max(180),
    configurationReleaseLabel: platformCopyTemplateSchema(['{number}'], 120),
    configurationPublishKindLabel: z.string().trim().min(1).max(80),
    configurationRollbackKindLabel: z.string().trim().min(1).max(80),
    configurationActiveReleaseLabel: z.string().trim().min(1).max(80),
    configurationPrepareRollbackAction: z.string().trim().min(1).max(100),
    configurationConfirmRollbackAction: z.string().trim().min(1).max(100),
    configurationCancelAction: z.string().trim().min(1).max(100),
    configurationDraftSaveError: z.string().trim().min(1).max(240),
    configurationPublishError: z.string().trim().min(1).max(240),
    configurationRollbackError: z.string().trim().min(1).max(240),
});
const platformLightThemePaletteFallback = {
    canvas: '#f5f7fb',
    surface: '#ffffff',
    surfaceElevated: '#ffffff',
    text: '#172033',
    textMuted: '#64748b',
    border: '#dbe2ea',
    brand: '#0f5f73',
    brandContrast: '#ffffff',
    success: '#16794d',
    warning: '#a35c00',
    danger: '#b42318',
    focus: '#2563eb',
    attention: '#a35c00',
    attentionContrast: '#ffffff',
};
const platformDarkThemePaletteFallback = {
    canvas: '#0b1220',
    surface: '#111827',
    surfaceElevated: '#172033',
    text: '#f8fafc',
    textMuted: '#cbd5e1',
    border: '#64748b',
    brand: '#38bdf8',
    brandContrast: '#082f49',
    success: '#4ade80',
    warning: '#fbbf24',
    danger: '#fb7185',
    focus: '#facc15',
    attention: '#fbbf24',
    attentionContrast: '#082f49',
};
const platformThemeTokensV2CodedFallback = {
    defaultMode: 'system',
    color: {
        canvas: '#f5f7fb', surface: '#ffffff', surfaceElevated: '#ffffff', text: '#172033',
        textMuted: '#64748b', border: '#dbe2ea', brand: '#0f5f73', brandContrast: '#ffffff',
        success: '#16794d', warning: '#a35c00', danger: '#b42318', focus: '#2563eb',
    },
    darkColor: {
        canvas: '#0b1220', surface: '#111827', surfaceElevated: '#172033', text: '#f8fafc',
        textMuted: '#cbd5e1', border: '#64748b', brand: '#38bdf8', brandContrast: '#082f49',
        success: '#4ade80', warning: '#fbbf24', danger: '#fb7185', focus: '#facc15',
    },
    typography: { fontFamily: 'system', baseSizePx: 16, lineHeight: 1.5, headingScale: 1.25 },
    card: { radiusPx: 14, paddingPx: 20, gapPx: 16, minWidthPx: 280, shadow: 'subtle' },
};
const platformThemeTokensCodedFallback = {
    presetId: 'crest-neutral-v1',
    presetChecksum: '36e204978d1c7803cb17b803d5983dd0e9ea4d2d3f030e79549ac78bd3c9e0b5',
    defaultMode: 'system',
    color: platformLightThemePaletteFallback,
    darkColor: platformDarkThemePaletteFallback,
    typography: {
        bodyFontFamily: 'system',
        headingFontFamily: 'system',
        baseSizePx: 16,
        lineHeight: 1.5,
        headingScale: 1.25,
    },
    card: {
        radiusPx: 14,
        paddingPx: 20,
        gapPx: 16,
        minWidthPx: 280,
        maxWidthPx: null,
        elevation: 'subtle',
    },
    control: { radiusPx: 10, minHeightPx: 44 },
    button: { shape: 'rounded', radiusPx: 10, minHeightPx: 44, fieldActionMinHeightPx: 56 },
    motion: { standardDurationMs: 160, easing: 'ease-out' },
};
const platformShellLayoutCodedFallback = {
    sidebarWidthPx: 264,
    contentMaxWidthPx: 1440,
    density: 'comfortable',
    navigationLabels: 'full',
};
const platformAccessibilityPreferencesCodedFallback = {
    contrast: 'standard',
    reduceMotion: false,
    underlineLinks: false,
    focusIndicator: 'standard',
};
function canonicalJson(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    const record = value;
    return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(',')}}`;
}
function immutableCopy(value) {
    if (value === null || typeof value !== 'object')
        return value;
    if (Array.isArray(value))
        return Object.freeze(value.map(immutableCopy));
    return Object.freeze(Object.fromEntries(Object.entries(value).map(([key, entry]) => [
        key,
        immutableCopy(entry),
    ])));
}
function themeSemanticValue(theme) {
    const { presetId: _presetId, presetChecksum: _presetChecksum, ...semanticTheme } = theme;
    return semanticTheme;
}
function appearanceProjection(theme, layout, accessibility) {
    return {
        'platform.accessibility.preferences': accessibility,
        'platform.layout.shell': layout,
        'platform.theme.tokens': themeSemanticValue(theme),
    };
}
function appearanceChecksum(theme, layout, accessibility) {
    return bytesToHex(sha256(new TextEncoder().encode(canonicalJson(appearanceProjection(theme, layout, accessibility)))));
}
const operationalThemeBase = {
    ...platformThemeTokensCodedFallback,
    presetId: 'crest-operational-classic-v1',
    presetChecksum: 'accb12c333c04a64bd64838970891bf7616e51948e1eb880fe869dc24837acb5',
    color: {
        ...platformLightThemePaletteFallback,
        canvas: '#f5f8fb',
        text: '#0f172a',
        textMuted: '#475569',
        border: '#cbd5e1',
        brand: '#1479be',
        focus: '#2b86c8',
    },
    card: {
        radiusPx: 8,
        paddingPx: 16,
        gapPx: 12,
        minWidthPx: 280,
        maxWidthPx: 720,
        elevation: 'subtle',
    },
    control: { radiusPx: 8, minHeightPx: 44 },
    button: { shape: 'rounded', radiusPx: 8, minHeightPx: 44, fieldActionMinHeightPx: 56 },
};
const operationalLayout = { ...platformShellLayoutCodedFallback, sidebarWidthPx: 296 };
const highContrastAccessibility = {
    contrast: 'high',
    reduceMotion: true,
    underlineLinks: true,
    focusIndicator: 'enhanced',
};
const highContrastThemeBase = {
    ...operationalThemeBase,
    presetId: 'field-high-contrast-v1',
    presetChecksum: 'a29e015e000f33b4d9084d15f6d1d55c5409f8ea79cca81cc8037cac12e40494',
    color: {
        canvas: '#ffffff', surface: '#ffffff', surfaceElevated: '#ffffff', text: '#000000',
        textMuted: '#1f2937', border: '#000000', brand: '#0047ab', brandContrast: '#ffffff',
        success: '#006b3c', warning: '#7a3e00', danger: '#b00020', focus: '#000000',
        attention: '#ffd166', attentionContrast: '#000000',
    },
    darkColor: {
        canvas: '#000000', surface: '#0a0a0a', surfaceElevated: '#111111', text: '#ffffff',
        textMuted: '#e5e7eb', border: '#ffffff', brand: '#66b2ff', brandContrast: '#001a33',
        success: '#4ade80', warning: '#fbbf24', danger: '#fb7185', focus: '#ffff00',
        attention: '#ffff00', attentionContrast: '#000000',
    },
    typography: {
        bodyFontFamily: 'system', headingFontFamily: 'atkinson-hyperlegible',
        baseSizePx: 18, lineHeight: 1.6, headingScale: 1.25,
    },
    control: { radiusPx: 8, minHeightPx: 48 },
    button: { shape: 'rounded', radiusPx: 8, minHeightPx: 48, fieldActionMinHeightPx: 64 },
};
const waterProThemeBase = {
    ...operationalThemeBase,
    presetId: 'water-pro-classic-v1',
    presetChecksum: '5ed2e98b29397f6c702384d7537e658c7f8cc725dedf17c6a1846bb560533c92',
    color: { ...operationalThemeBase.color, attention: '#e7701e', attentionContrast: '#0f172a' },
};
const waterProThemeV2Base = {
    ...waterProThemeBase,
    presetId: 'water-pro-classic-v2',
    presetChecksum: '44be50d2bf2dec12449d15c97af2b30bed502b13d19a051b1867767c832a241b',
    defaultMode: 'light',
};
export const platformTenantAppearancePresetCatalog = immutableCopy({
    'crest-neutral-v1': {
        theme: platformThemeTokensCodedFallback,
        layout: platformShellLayoutCodedFallback,
        accessibility: platformAccessibilityPreferencesCodedFallback,
    },
    'crest-operational-classic-v1': {
        theme: operationalThemeBase,
        layout: operationalLayout,
        accessibility: platformAccessibilityPreferencesCodedFallback,
    },
    'field-high-contrast-v1': {
        theme: highContrastThemeBase,
        layout: operationalLayout,
        accessibility: highContrastAccessibility,
    },
    'water-pro-classic-v1': {
        theme: waterProThemeBase,
        layout: operationalLayout,
        accessibility: platformAccessibilityPreferencesCodedFallback,
    },
    'water-pro-classic-v2': {
        theme: waterProThemeV2Base,
        layout: operationalLayout,
        accessibility: platformAccessibilityPreferencesCodedFallback,
    },
});
export function classifyPlatformTenantAppearanceBase(value) {
    const source = typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : {};
    const theme = typeof source['platform.theme.tokens'] === 'object' &&
        source['platform.theme.tokens'] !== null && !Array.isArray(source['platform.theme.tokens'])
        ? source['platform.theme.tokens']
        : {};
    const layout = typeof source['platform.layout.shell'] === 'object' &&
        source['platform.layout.shell'] !== null && !Array.isArray(source['platform.layout.shell'])
        ? source['platform.layout.shell']
        : {};
    const accessibility = typeof source['platform.accessibility.preferences'] === 'object' &&
        source['platform.accessibility.preferences'] !== null &&
        !Array.isArray(source['platform.accessibility.preferences'])
        ? source['platform.accessibility.preferences']
        : {};
    const projection = appearanceProjection(theme, layout, accessibility);
    const canonicalProjection = canonicalJson(projection);
    for (const [presetId, preset] of Object.entries(platformTenantAppearancePresetCatalog)) {
        if (canonicalProjection === canonicalJson(appearanceProjection(preset.theme, preset.layout, preset.accessibility))) {
            return immutableCopy({ presetId: presetId, presetChecksum: preset.theme.presetChecksum });
        }
    }
    return immutableCopy({ presetId: 'custom-v1', presetChecksum: appearanceChecksum(theme, layout, accessibility) });
}
function recordOrEmpty(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : {};
}
function legacyV2Projection(theme) {
    const typography = recordOrEmpty(theme.typography);
    const card = recordOrEmpty(theme.card);
    const { attention: _attention, attentionContrast: _attentionContrast, ...color } = recordOrEmpty(theme.color);
    const { attention: _darkAttention, attentionContrast: _darkAttentionContrast, ...darkColor } = recordOrEmpty(theme.darkColor);
    return {
        defaultMode: theme.defaultMode,
        color,
        darkColor,
        typography: {
            fontFamily: typography.bodyFontFamily,
            baseSizePx: typography.baseSizePx,
            lineHeight: typography.lineHeight,
            headingScale: typography.headingScale,
        },
        card: {
            radiusPx: card.radiusPx,
            paddingPx: card.paddingPx,
            gapPx: card.gapPx,
            minWidthPx: card.minWidthPx,
            shadow: card.elevation,
        },
    };
}
const operationalThemeV2 = immutableCopy(legacyV2Projection(operationalThemeBase));
function liftV2Theme(value) {
    const source = recordOrEmpty(value);
    const color = recordOrEmpty(source.color);
    const darkColor = recordOrEmpty(source.darkColor);
    const typography = recordOrEmpty(source.typography);
    const card = recordOrEmpty(source.card);
    const genericTheme = {
        ...platformThemeTokensCodedFallback,
        color: { ...platformThemeTokensCodedFallback.color, ...color },
        darkColor: { ...platformThemeTokensCodedFallback.darkColor, ...darkColor },
        typography: {
            bodyFontFamily: typeof typography.fontFamily === 'string'
                ? typography.fontFamily
                : platformThemeTokensCodedFallback.typography.bodyFontFamily,
            headingFontFamily: typeof typography.fontFamily === 'string'
                ? typography.fontFamily
                : platformThemeTokensCodedFallback.typography.headingFontFamily,
            baseSizePx: typography.baseSizePx ?? platformThemeTokensCodedFallback.typography.baseSizePx,
            lineHeight: typography.lineHeight ?? platformThemeTokensCodedFallback.typography.lineHeight,
            headingScale: typography.headingScale ?? platformThemeTokensCodedFallback.typography.headingScale,
        },
        card: {
            radiusPx: card.radiusPx ?? platformThemeTokensCodedFallback.card.radiusPx,
            paddingPx: card.paddingPx ?? platformThemeTokensCodedFallback.card.paddingPx,
            gapPx: card.gapPx ?? platformThemeTokensCodedFallback.card.gapPx,
            minWidthPx: card.minWidthPx ?? platformThemeTokensCodedFallback.card.minWidthPx,
            maxWidthPx: null,
            elevation: card.shadow ?? platformThemeTokensCodedFallback.card.elevation,
        },
    };
    const isOperational = canonicalJson(source) ===
        canonicalJson(operationalThemeV2);
    const semanticTheme = isOperational ? { ...operationalThemeBase } : genericTheme;
    const layout = isOperational ? operationalLayout : platformShellLayoutCodedFallback;
    const classification = classifyPlatformTenantAppearanceBase(appearanceProjection(semanticTheme, layout, platformAccessibilityPreferencesCodedFallback));
    return { ...semanticTheme, ...classification };
}
export const platformThemeTokensSetting = definePlatformSetting({
    definition: {
        key: 'platform.theme.tokens',
        moduleId: 'platform',
        title: 'Theme tokens',
        helpText: 'Controls tenant colors, typography, and card geometry across client surfaces.',
        configurationDomain: 'theme',
        valueType: 'object',
        schemaVersion: 3,
        codedFallback: platformThemeTokensCodedFallback,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformThemeTokensValueSchema,
    migrations: [
        {
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => {
                const source = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? value
                    : {};
                const legacyColor = typeof source.color === 'object' && source.color !== null &&
                    !Array.isArray(source.color)
                    ? source.color
                    : {};
                return {
                    ...platformThemeTokensV2CodedFallback,
                    ...source,
                    defaultMode: 'system',
                    darkColor: {
                        ...platformDarkThemePaletteFallback,
                        ...(typeof legacyColor.brand === 'string' ? { brand: legacyColor.brand } : {}),
                        ...(typeof legacyColor.brandContrast === 'string'
                            ? { brandContrast: legacyColor.brandContrast }
                            : {}),
                    },
                };
            },
        },
        {
            fromVersion: 2,
            toVersion: 3,
            migrate: liftV2Theme,
        },
    ],
});
export const platformBrandingIdentitySetting = definePlatformSetting({
    definition: {
        key: 'platform.branding.identity',
        moduleId: 'platform',
        title: 'Brand identity',
        helpText: 'Controls the company identity rendered in CREST tenant experiences.',
        configurationDomain: 'branding',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            applicationName: 'CREST',
            companyDisplayName: 'CREST',
            legalBusinessName: null,
            businessAddress: null,
            supportEmail: null,
            supportPhone: null,
            assets: {
                lightLogo: null,
                darkLogo: null,
                printLogo: null,
                emailLogo: null,
                portalLogo: null,
                documentLogo: null,
                favicon: null,
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformBrandingIdentityValueSchema,
    migrations: [
        {
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => {
                const source = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? value
                    : {};
                return {
                    applicationName: typeof source.applicationName === 'string'
                        ? source.applicationName
                        : 'CREST',
                    companyDisplayName: typeof source.companyDisplayName === 'string'
                        ? source.companyDisplayName
                        : 'CREST',
                    legalBusinessName: null,
                    businessAddress: null,
                    supportEmail: typeof source.supportEmail === 'string' ? source.supportEmail : null,
                    supportPhone: typeof source.supportPhone === 'string' ? source.supportPhone : null,
                    assets: {
                        lightLogo: null,
                        darkLogo: null,
                        printLogo: null,
                        emailLogo: null,
                        portalLogo: null,
                        documentLogo: null,
                        favicon: null,
                    },
                };
            },
        },
    ],
});
export const platformCustomDomainSelectionSetting = definePlatformSetting({
    definition: {
        key: 'platform.custom-domains.selection',
        moduleId: 'platform',
        title: 'Custom domains',
        helpText: 'Selects exact verified custom-domain versions for shared web and portal surfaces.',
        configurationDomain: 'branding',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: { webDomain: null, portalDomain: null },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'operational-reference',
    },
    valueSchema: platformCustomDomainSelectionValueSchema,
});
export const platformSenderIdentitySelectionSetting = definePlatformSetting({
    definition: {
        key: 'platform.sender-identities.selection',
        moduleId: 'platform',
        title: 'Sender identities',
        helpText: 'Selects exact verified email and SMS sender identity versions.',
        configurationDomain: 'branding',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: { emailSender: null, smsSender: null },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'operational-reference',
    },
    valueSchema: platformSenderIdentitySelectionValueSchema,
});
export const platformShellLayoutSetting = definePlatformSetting({
    definition: {
        key: 'platform.layout.shell',
        moduleId: 'platform',
        title: 'Application layout',
        helpText: 'Controls shell width, navigation density, and content sizing.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: platformShellLayoutCodedFallback,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformShellLayoutValueSchema,
});
export const platformAccessibilityPreferencesSetting = definePlatformSetting({
    definition: {
        key: 'platform.accessibility.preferences',
        moduleId: 'platform',
        title: 'Accessibility preferences',
        helpText: 'Controls tenant defaults that improve legibility, focus, and motion comfort.',
        configurationDomain: 'accessibility',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: platformAccessibilityPreferencesCodedFallback,
        permittedScopes: ['platform', 'tenant', 'user', 'device'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformAccessibilityPreferencesValueSchema,
});
export const platformSystemNotificationConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'platform.system-notifications.configuration',
        moduleId: 'platform',
        title: 'Notification polling',
        helpText: 'Controls how often the notification bell refreshes and how many items it loads first.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: platformSystemNotificationDefaults.configuration,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformSystemNotificationConfigurationValueSchema,
});
export const platformSystemNotificationDisplaySetting = definePlatformSetting({
    definition: {
        key: 'platform.system-notifications.display',
        moduleId: 'platform',
        title: 'Notification display',
        helpText: 'Controls notification density, timestamps, date grouping, and panel height.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: platformSystemNotificationDefaults.display,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformSystemNotificationDisplayValueSchema,
});
export const platformSystemNotificationCopySetting = definePlatformSetting({
    definition: {
        key: 'platform.copy.system-notifications',
        moduleId: 'platform',
        title: 'Notification text',
        helpText: 'Controls tenant-editable labels and status messages in the shared notification bell.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: platformSystemNotificationDefaults.copy,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformSystemNotificationCopyValueSchema,
});
export const platformMyDayCopySetting = definePlatformSetting({
    definition: {
        key: 'platform.copy.my-day',
        moduleId: 'platform',
        title: 'My Day host copy',
        helpText: 'Controls tenant-editable labels for the shared My Day host and its optional panels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            navigationLabel: 'My Day',
            title: 'My Day',
            description: 'Today\'s jobs, time, tasks, and priorities in one place.',
            loadingLabel: 'Loading My Day…',
            contributionError: 'One My Day panel could not be loaded.',
            emptyTitle: 'Your day is clear',
            emptyDescription: 'Available modules will add relevant work and attention items here.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformMyDayCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: migratePlatformMyDayCopyVersionOne,
        }],
});
const platformCoreCopyCodedFallback = {
    navigationProjects: 'Projects',
    navigationGroupCommand: 'Command',
    navigationGroupProjects: 'Projects',
    navigationGroupField: 'Field',
    navigationGroupMoney: 'Money',
    navigationGroupRelationships: 'Customers & Growth',
    navigationGroupEstimating: 'External Estimating',
    navigationGroupReports: 'Reports',
    navigationGroupAdmin: 'Admin',
    accountMenuLabel: 'Account',
    settingsLabel: 'Settings',
    primaryNavigationLabel: 'Primary navigation',
    skipToContentLabel: 'Skip to main content',
    noAvailableModulesLabel: 'No application modules are available for this account.',
    loadingLabel: 'Loading CREST…',
    unexpectedErrorLabel: 'Something went wrong. Please try again.',
    configurationTitle: 'Tenant configuration',
    configurationDescription: 'Every publish creates an immutable release. Rollback creates another release and never deletes history.',
    configurationSectionLabel: 'Configuration section',
    configurationHistoryLoadError: 'Configuration history could not be loaded.',
    configurationDraftExistsLabel: 'An open backed-up draft exists for this section.',
    configurationDraftSchemaOutdatedLabel: 'This draft uses an older settings schema. Review and save it again before publishing.',
    configurationDraftSavedMessage: 'Draft version {version} is backed up and ready to publish.',
    configurationAdvancedSummary: 'Validated JSON value',
    configurationAdvancedLabel: 'Advanced JSON editor',
    configurationSaveDraftAction: 'Save backed-up draft',
    configurationPublishAction: 'Publish validated change',
    configurationInitializeBaselineAction: 'Create rollback baseline',
    configurationInitializeBaselineHelp: 'Before the first tenant change, save the coded defaults as immutable release 1.',
    configurationBaselineRequiredLabel: 'Create the initial rollback baseline before validating tenant drafts.',
    configurationReasonLabel: 'Reason for this change',
    configurationReasonHelp: 'Required for the immutable tenant history. Enter 10 to 500 characters.',
    configurationReasonPlaceholder: 'Explain what is changing and why.',
    configurationValidatePreviewAction: 'Validate publish preview',
    configurationValidateRollbackAction: 'Validate rollback preview',
    configurationPreviewValidatedMessage: 'Server preview validated. Integrity checksum: {checksum}',
    configurationPreviewRequiredLabel: 'Validate a server preview before publishing this change.',
    configurationPublishPermissionRequiredLabel: 'Your role can prepare previews but cannot publish or roll back tenant configuration.',
    configurationPreviewExpiredError: 'The validated preview expired. Validate it again before publishing.',
    configurationReauthenticationError: 'Sign in again before publishing this protected tenant change.',
    configurationPreviewTitle: 'Live preview',
    configurationPreviewInvalidLabel: 'Fix validation errors to preview.',
    configurationPreviewCardTitle: 'Project card preview',
    configurationPreviewCardDescription: 'Colors, text, spacing, sizing, and typography update here.',
    configurationPreviewAction: 'Preview action',
    configurationHistoryTitle: 'Release history',
    configurationNoReleasesLabel: 'No tenant releases have been published.',
    configurationReleaseLabel: 'Release {number}',
    configurationPublishKindLabel: 'Published',
    configurationRollbackKindLabel: 'Rollback',
    configurationActiveReleaseLabel: 'Active',
    configurationPrepareRollbackAction: 'Prepare rollback',
    configurationConfirmRollbackAction: 'Confirm rollback',
    configurationCancelAction: 'Cancel',
    configurationDraftSaveError: 'The configuration draft could not be saved. An open draft may already exist.',
    configurationPublishError: 'Publish was rejected. Reload the page before retrying.',
    configurationRollbackError: 'Rollback was rejected. Reload the page before retrying.',
};
function platformCoreCopyVersionTwoFallback() {
    const fallback = { ...platformCoreCopyCodedFallback };
    for (const key of [
        'configurationReasonLabel',
        'configurationInitializeBaselineAction',
        'configurationInitializeBaselineHelp',
        'configurationBaselineRequiredLabel',
        'configurationReasonHelp',
        'configurationReasonPlaceholder',
        'configurationValidatePreviewAction',
        'configurationValidateRollbackAction',
        'configurationPreviewValidatedMessage',
        'configurationPreviewRequiredLabel',
        'configurationPublishPermissionRequiredLabel',
        'configurationPreviewExpiredError',
        'configurationReauthenticationError',
    ])
        delete fallback[key];
    return fallback;
}
export const platformCoreCopySetting = definePlatformSetting({
    definition: {
        key: 'platform.copy.core',
        moduleId: 'platform',
        title: 'Core interface copy',
        helpText: 'Controls tenant-editable labels in the shared application shell.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 4,
        codedFallback: platformCoreCopyCodedFallback,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: platformCoreCopyValueSchema,
    migrations: [
        {
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...platformCoreCopyVersionTwoFallback(),
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        },
        {
            fromVersion: 2,
            toVersion: 3,
            migrate: (value) => ({
                ...platformCoreCopyCodedFallback,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        },
        {
            fromVersion: 3,
            toVersion: 4,
            migrate: (value) => ({
                ...platformCoreCopyCodedFallback,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        },
    ],
});
export const platformSettingRegistrations = [
    platformThemeTokensSetting,
    platformBrandingIdentitySetting,
    platformCustomDomainSelectionSetting,
    platformSenderIdentitySelectionSetting,
    platformShellLayoutSetting,
    platformAccessibilityPreferencesSetting,
    platformMyDayCopySetting,
    platformCoreCopySetting,
    platformSystemNotificationConfigurationSetting,
    platformSystemNotificationDisplaySetting,
    platformSystemNotificationCopySetting,
];
//# sourceMappingURL=platform-setting-definitions.js.map